/**
 * Közös típus-kontraktus. Minden modul (pálya, AI, itemek, HUD, render) ezt használja.
 * Szabály: ez a fájl a "szerződés" – csak az integrátor módosíthatja.
 */

export interface Vec2 {
  x: number;
  y: number;
}

/** Milyen felületen gurul a kart. */
export type SurfaceKind = 'road' | 'kerb' | 'sidewalk' | 'boost';

/** 0..10 skálán értelmezett karakterstatok. A kiegyensúlyozás a docs/01-ben leírt pontkeret szerint történik. */
export interface KartStats {
  topSpeed: number;
  accel: number;
  handling: number;
  grip: number;
  weight: number;
}

export type VehicleKind =
  | 'kart'
  | 'scooter'
  | 'minivan'
  | 'wagon'
  | 'courier'
  | 'van'
  | 'sedan'
  | 'beetle'
  | 'taxi'
  | 'hatch';

export interface AbilityDef {
  id: string;
  name: string;
  desc: string;
  /** másodperc */
  cooldown: number;
  /** hatás hossza másodpercben */
  duration: number;
}

export interface CharacterDef {
  id: string;
  name: string;
  /** rövid név a HUD-ra, pl. "Soma" */
  short: string;
  age: number;
  /** pl. "A 12 éves gokartos" */
  role: string;
  bio: string;
  colors: {
    body: string;
    accent: string;
    skin: string;
    hair: string;
  };
  vehicle: {
    kind: VehicleKind;
    name: string;
    /** világ-pixel */
    length: number;
    width: number;
  };
  stats: KartStats;
  ability: AbilityDef;
  /** az alap 4 választható karakter; a riválisoknál false */
  selectable: boolean;
}

export interface InputState {
  /** -1 = balra, +1 = jobbra */
  steer: number;
  throttle: number;
  brake: number;
  drift: boolean;
  /** élre triggerelt: csak a lenyomás pillanatában igaz */
  useItem: boolean;
  /** élre triggerelt képesség */
  ability: boolean;
}

export const IDLE_INPUT: InputState = {
  steer: 0,
  throttle: 0,
  brake: 0,
  drift: false,
  useItem: false,
  ability: false,
};

export interface Viewport {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Egy kamera/nézet ablaka az osztott képernyőn. */
export interface CameraView extends Viewport {
  /** világkoordináta, amit a nézet közepe mutat */
  cx: number;
  cy: number;
  /** px / világpx */
  zoom: number;
  /** enyhe döntés: a világ Y tengelyének zsugorítása */
  yScale: number;
  /** látható világ-téglalap (cullinghoz) */
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
}

export type PropKind =
  | 'building'
  | 'tree'
  | 'lamp'
  | 'tram'
  | 'bench'
  | 'kiosk'
  | 'sign'
  | 'hydrant'
  | 'bin'
  | 'parkedCar'
  | 'crosswalk'
  | 'arrow'
  | 'puddle'
  | 'cone';

export interface PropDef {
  kind: PropKind;
  /** pálya menti pozíció 0..1 */
  s: number;
  /** középvonaltól mért oldalirányú eltolás, px (negatív = bal) */
  lateral: number;
  scale?: number;
  variant?: number;
}

export interface BoostPadDef {
  s: number;
  lateral: number;
  /** px, a pálya mentén */
  length: number;
  /** px, oldalirányban */
  width: number;
}

export interface ItemRowDef {
  s: number;
  lateral: number;
  count?: number;
  /** px */
  spacing?: number;
}

export interface AmbientPalette {
  sky: string;
  asphalt: string;
  asphaltDark: string;
  lane: string;
  kerb: string;
  sidewalk: string;
  wall: string;
  accent: string;
  shadow: string;
}

export interface TrackDef {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  /** zárt hurkot leíró vezérlőpontok, világ-pixelben */
  centerline: Vec2[];
  roadHalfWidth: number;
  kerbWidth: number;
  sidewalkWidth: number;
  /** rajtvonal pozíciója 0..1 */
  startS: number;
  laps: number;
  props: PropDef[];
  boostPads: BoostPadDef[];
  itemRows: ItemRowDef[];
  ambient: AmbientPalette;
  /** ajánlott kamera zoom 1 játékosnál */
  cameraZoom?: number;
}

export interface TrackSample {
  pos: Vec2;
  tangent: Vec2;
  normal: Vec2;
  /** ívhossz a rajtvonaltól számítva, px */
  s: number;
  curvature: number;
}

export interface SurfaceInfo {
  kind: SurfaceKind;
  /** előjeles oldalirányú távolság a középvonaltól (bal negatív) */
  lateral: number;
  /** ívhossz, px */
  s: number;
  closest: Vec2;
  dist: number;
  /** a falon túl van (a korridor szélén kívül) */
  outside: boolean;
}

export type ItemId = 'turbo' | 'oil' | 'soda' | 'lightning' | 'shield' | 'glue';

export type ItemKind = 'self' | 'projectile' | 'drop' | 'global';

export interface ItemDef {
  id: ItemId;
  name: string;
  /** rövid felirat / emoji a HUD-ra */
  icon: string;
  color: string;
  kind: ItemKind;
  blurb: string;
  /** melyik helyezéseknél nagyobb az esély (0 = első, 1 = utolsó) */
  comeBack?: boolean;
}

export interface ItemBox {
  id: number;
  pos: Vec2;
  active: boolean;
  /** visszatöltődés hátralévő ideje */
  respawn: number;
  /** vizuális pörgés fázisa */
  phase: number;
}

export interface Hazard {
  id: number;
  kind: ItemId;
  pos: Vec2;
  radius: number;
  ownerId: number;
  /** hátralévő élettartam, s */
  life: number;
  /** már kiváltotta-e a hatást */
  spent: boolean;
}

export interface Projectile {
  id: number;
  kind: ItemId;
  pos: Vec2;
  vel: Vec2;
  radius: number;
  ownerId: number;
  life: number;
  /** már eltalált valakit */
  spent: boolean;
}

export type ItemEventKind = 'pickup' | 'roll' | 'use' | 'hit' | 'blocked' | 'explode' | 'bounce';

export interface ItemEvent {
  kind: ItemEventKind;
  item: ItemId;
  at: Vec2;
  ownerId?: number;
  targetId?: number;
}

export type RacePhase = 'countdown' | 'racing' | 'finished';

export interface RaceConfig {
  mode: 1 | 2 | 4;
  laps: number;
  trackId: string;
  /** kiválasztott karakterek id-ji, mód sorrendjében */
  picks: string[];
  seed: number;
  /** 0..1, az AI erőssége */
  difficulty: number;
  /** csak teszteléshez: az emberi versenyzőket is AI vezeti */
  autopilot?: boolean;
  /** gyakorló mód: nincs AI-ellenfél */
  practice?: boolean;
  /** battle mód: 2v2 csapatcsata életekkel */
  battle?: boolean;
  /** éjszakai mód: sötét pálya, fényszórók */
  night?: boolean;
  /** nincs AI-ellenfél (PvP) */
  noAi?: boolean;
  /** csak méréshez: a karakterképességek kikapcsolása */
  noAbilities?: boolean;
}

export interface StandingEntry {
  kartId: number;
  name: string;
  color: string;
  position: number;
  progress: number;
  isPlayer: boolean;
  finished: boolean;
  /** battle: hátralévő életek */
  lives?: number;
  /** battle: csapat (0 = piros, 1 = kék) */
  team?: number;
}

export interface HudModel {
  kartId: number;
  position: number;
  total: number;
  lap: number;
  laps: number;
  speedKmh: number;
  item: ItemDef | null;
  /** rulett pörög */
  itemRolling: boolean;
  /** 0..1 rulett */
  itemProgress: number;
  /** 0..1 drift töltés */
  driftCharge: number;
  ability: { name: string; ready: boolean; ratio: number };
  shield: number;
  boost: number;
  /** hátralévő lassítás másodpercben (villám, ragacs) */
  slow: number;
  /** lassítás erőssége 0..1 (1 = nincs lassítás) */
  slowFactor: number;
  /** pillanatnyi felület */
  surface: SurfaceKind;
  standings: StandingEntry[];
  /** 3,2,1,0 – 0 = GO */
  countdown: number | null;
  raceTime: number;
  lapTime: number;
  bestLap: number | null;
  trackName: string;
  minimap: HTMLCanvasElement | null;
  /** gyakorló mód (egyedül, AI nélkül) */
  practice?: boolean;
  /** battle mód adatai */
  battle?: {
    team: number;
    teamColor: string;
    teamName: string;
    lives: number;
    teamLives: [number, number];
    timeLeft: number;
    out: boolean;
  };
  /** a minimap canvas 0..1 koordinátáin elhelyezett pontok */
  minimapDots?: { x: number; y: number; color: string; isPlayer: boolean }[];
  /** rövid üzenet, pl. "2. KÖR", "CÉL!" */
  message: string | null;
}

export interface RaceResultEntry {
  kartId: number;
  name: string;
  short: string;
  color: string;
  characterId: string;
  isPlayer: boolean;
  place: number;
  /** összidő másodpercben */
  time: number;
  bestLap: number | null;
  laps: number;
  finished: boolean;
  /** battle: hátralévő életek és csapat */
  lives?: number;
  team?: number;
}

export interface RaceResult {
  trackName: string;
  mode: number;
  entries: RaceResultEntry[];
  /** battle összegzés */
  battle?: {
    winningTeam: number;
    teams: { name: string; color: string; lives: number }[];
  };
}

export interface MenuResult {
  mode: 1 | 2 | 4;
  picks: string[];
  /** gyakorló mód: egyedül, AI nélkül */
  practice?: boolean;
  /** 2v2 csapatcsata */
  battle?: boolean;
  /** éjszakai pálya */
  night?: boolean;
  /** PvP: nincs AI-ellenfél */
  noAi?: boolean;
}
